/**
 * Tobacco excise fee auto-add — storefront/cart-page JS.
 *
 * Watches the cart. For every tobacco SKU present, ensures the matching
 * pre-priced excise fee variant is also in the cart at the right quantity,
 * keyed off the ship-state cart attribute `_ship_state` (written by the
 * Checkout UI extension during checkout shipping-address entry).
 *
 * - If state is unknown at cart-page time: shows an estimate banner only.
 * - If state is known and tobacco lines exist: auto-adds matching fee variant(s)
 *   to the cart at variant-level quantities equal to the tobacco line quantity.
 * - If tobacco line quantity changes: updates fee line quantity to match.
 * - If tobacco line removed: removes the matching fee line.
 *
 * Drop this file into the theme as `assets/tobacco-excise.js` and include
 * with `{{ 'tobacco-excise.js' | asset_url | script_tag }}` in theme.liquid.
 *
 * The orders/create webhook reconciles the actual ship-to state post-purchase
 * and flags any discrepancy in the order metafield, so this script is best-
 * effort: when it works the customer sees the right total at cart time, when
 * it doesn't (JS disabled, Shop Pay one-click bypass) the post-purchase
 * reconciliation still catches it.
 */

(function () {
  'use strict';

  // Per-(SKU, state) fee variant ID map. Generated by
  // metafields/create_per_sku_fee_variants.py — keep in sync after rate or
  // variant changes.
  var FEE_VARIANT_MAP = {
    BFSAMPLER4CT:  { OH: 51699926204632, MD: 51699926171864, IN: 51699926139096 },
    CHURCHILL:     { OH: 51699926302936, MD: 51699926270168, IN: 51699926237400 },
    CROBUSTO:      { OH: 51699926401240, MD: 51699926368472, IN: 51699926335704 },
    CTORO:         { OH: 51699926499544, MD: 51699926466776, IN: 51699926434008 },
    FLYINGPIG:     { OH: 51699926630616, MD: 51699926565080, IN: 51699926532312 },
    LIMITADA:      { OH: 51699926794456, MD: 51699926728920, IN: 51699926663384 },
    TRADBELICOSO:  { OH: 51699926892760, MD: 51699926859992, IN: 51699926827224 },
    TRADCHURCHILL: { OH: 51699926991064, MD: 51699926958296, IN: 51699926925528 },
    TRADROBUSTO:   { OH: 51699927089368, MD: 51699927056600, IN: 51699927023832 },
    TRADTORO:      { OH: 51699927187672, MD: 51699927154904, IN: 51699927122136 },
  };

  var SUPPORTED_STATES = ['OH', 'MD', 'IN'];

  /** All known excise variant IDs across SKUs (for distinguishing fee lines from tobacco lines). */
  var ALL_FEE_VARIANT_IDS = (function () {
    var set = Object.create(null);
    Object.keys(FEE_VARIANT_MAP).forEach(function (sku) {
      Object.keys(FEE_VARIANT_MAP[sku]).forEach(function (st) {
        set[FEE_VARIANT_MAP[sku][st]] = true;
      });
    });
    return set;
  })();

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function shipStateFromCart(cart) {
    var raw = (cart.attributes && cart.attributes._ship_state) || '';
    raw = String(raw).trim().toUpperCase();
    return SUPPORTED_STATES.indexOf(raw) >= 0 ? raw : null;
  }

  /**
   * Classify each cart line as tobacco-parent, excise-fee, or unrelated.
   * Returns { tobaccoLines: [{sku, quantity}], feeLines: [{variant_id, quantity, key}] }.
   */
  function classifyLines(cart) {
    var tobacco = [];
    var fee = [];
    cart.items.forEach(function (item) {
      if (ALL_FEE_VARIANT_IDS[item.variant_id]) {
        fee.push({ variant_id: item.variant_id, quantity: item.quantity, key: item.key });
        return;
      }
      if (item.sku && FEE_VARIANT_MAP[item.sku.toUpperCase()]) {
        tobacco.push({ sku: item.sku.toUpperCase(), quantity: item.quantity });
      }
    });
    return { tobaccoLines: tobacco, feeLines: fee };
  }

  /**
   * Build the desired fee-line state for the given cart + state.
   * Returns { variant_id: quantity }.
   */
  function desiredFees(tobaccoLines, state) {
    var desired = {};
    tobaccoLines.forEach(function (t) {
      var perSku = FEE_VARIANT_MAP[t.sku];
      if (!perSku) return;
      var feeVariantId = perSku[state];
      if (!feeVariantId) return;
      desired[feeVariantId] = (desired[feeVariantId] || 0) + t.quantity;
    });
    return desired;
  }

  /**
   * Diff existing fee lines against desired and produce the minimum set of
   * cart updates needed: removals (qty 0), quantity adjustments, or new adds.
   */
  function diff(feeLines, desired) {
    var updates = {};  // variant_id (number) → quantity
    var adds = [];     // list of variant_ids to add (not in current cart)

    var existing = {};
    feeLines.forEach(function (f) { existing[f.variant_id] = f; });

    Object.keys(existing).forEach(function (vidStr) {
      var vid = Number(vidStr);
      var wanted = desired[vid] || 0;
      if (wanted === 0) {
        updates[vid] = 0;  // remove
      } else if (wanted !== existing[vid].quantity) {
        updates[vid] = wanted;
      }
    });

    Object.keys(desired).forEach(function (vidStr) {
      var vid = Number(vidStr);
      if (!existing[vid] && desired[vid] > 0) {
        adds.push({ id: vid, quantity: desired[vid] });
      }
    });

    return { updates: updates, adds: adds };
  }

  function applyUpdates(updates) {
    var keys = Object.keys(updates);
    if (keys.length === 0) return Promise.resolve();
    var body = { updates: {} };
    keys.forEach(function (vid) { body.updates[vid] = updates[vid]; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function applyAdds(adds) {
    if (adds.length === 0) return Promise.resolve();
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: adds }),
    });
  }

  // Re-entrancy guard: cart events can fire during our own updates.
  var syncing = false;

  function sync() {
    if (syncing) return Promise.resolve();
    syncing = true;
    return readCart()
      .then(function (cart) {
        var state = shipStateFromCart(cart);
        var c = classifyLines(cart);

        // If state is unknown OR no tobacco in cart, the only thing to do is
        // remove any stale fee lines (e.g. state changed away from a supported
        // one, or all tobacco was removed).
        if (!state || c.tobaccoLines.length === 0) {
          var removals = {};
          c.feeLines.forEach(function (f) { removals[f.variant_id] = 0; });
          return applyUpdates(removals);
        }

        var wanted = desiredFees(c.tobaccoLines, state);
        var d = diff(c.feeLines, wanted);
        return applyUpdates(d.updates).then(function () { return applyAdds(d.adds); });
      })
      .catch(function (err) {
        // Don't surface errors to the customer — webhook reconciliation
        // catches the under-collection silently.
        if (window.console && window.console.warn) {
          window.console.warn('[tobacco-excise] sync failed:', err);
        }
      })
      .then(function () { syncing = false; });
  }

  // Initial sync on page load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  // Subscribe to Shopify's standard cart events. Different themes fire
  // different events; we cover the common ones.
  ['cart:updated', 'cart:refresh', 'cart:item-added', 'cart:item-removed'].forEach(function (evt) {
    document.addEventListener(evt, function () { sync(); });
  });

  // Patch fetch to detect cart-mutating calls and re-sync afterwards.
  // (Themes that don't dispatch cart:* events still trigger this path.)
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
      p.then(function () { setTimeout(sync, 50); });
    }
    return p;
  };

  // Expose for debugging
  window.__tobaccoExcise = { sync: sync, FEE_VARIANT_MAP: FEE_VARIANT_MAP };
})();